/**
 * Pure helpers for "เมนูรอบนี้" (knowledge page › menu section, the mock, tests). No Vue imports.
 * Dates are YYYY-MM-DD in Bangkok; the round's valid_from / valid_to are both inclusive.
 */
import type {
  DishField,
  DishValues,
  LiveDish,
  MenuAlert,
  MenuDrift,
  MenuField,
  MenuNotes,
  MenuRound,
  MenuRoundState,
  OverrideMap,
  OverrideValue,
  RoundDish,
} from '../api/kb/menuTypes'

const DAY_MS = 86400000
const BKK_MS = 7 * 3600 * 1000
export const MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// ------------------------------------------------------------------ dates

export function bangkokToday(nowMs: number = Date.now()): string {
  return new Date(nowMs + BKK_MS).toISOString().slice(0, 10)
}

const ymdMs = (ymd: string): number => Date.parse(`${ymd}T00:00:00Z`)

export function addDaysYmd(ymd: string, n: number): string {
  return new Date(ymdMs(ymd) + n * DAY_MS).toISOString().slice(0, 10)
}

/** b − a in days */
export function diffDays(a: string, b: string): number {
  return Math.round((ymdMs(b) - ymdMs(a)) / DAY_MS)
}

/** Same day next month(s); the 31st becomes the month's last day. */
export function addMonthsYmd(ymd: string, n: number): string {
  const d = new Date(ymdMs(ymd))
  const y = d.getUTCFullYear()
  const m = d.getUTCMonth() + n
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
  return new Date(Date.UTC(y, m, Math.min(d.getUTCDate(), last))).toISOString().slice(0, 10)
}

export function isYmd(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(ymdMs(v))
}

/** "2026-09-26" → "26 ก.ย. 2569" (year = Buddhist era) · withYear false → "26 ก.ย." */
export function thaiDate(ymd: string, withYear = true): string {
  const d = new Date(ymdMs(ymd))
  if (Number.isNaN(d.getTime())) return ymd
  const s = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
  return withYear ? `${s} ${d.getUTCFullYear() + 543}` : s
}

/** "26 ก.ย. – 26 พ.ย. 2569" · across a year: "15 ธ.ค. 2569 – 15 ก.พ. 2570" */
export function periodText(from: string, to: string): string {
  if (from.slice(0, 4) === to.slice(0, 4)) return `${thaiDate(from, false)} – ${thaiDate(to)}`
  return `${thaiDate(from)} – ${thaiDate(to)}`
}

/** Length of a period for the hint next to the dates: "2 เดือน" when it ends on the same day of a later month. */
export function durationText(from: string, to: string): string {
  const days = diffDays(from, to)
  if (days <= 0) return days === 0 ? '1 วัน' : ''
  for (let m = 1; m <= 12; m++) {
    const end = addMonthsYmd(from, m)
    if (end === to) return `${m} เดือน`
    if (end > to) break
  }
  return `${days} วัน`
}

/** ISO timestamp → "26 ก.ย. 21:40" (Bangkok) */
export function thaiDateTime(iso: string | null): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t + BKK_MS)
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${hh}:${mm}`
}

// ------------------------------------------------------------------ fields + values

export const DEFAULT_FIELDS: MenuField[] = [
  { key: 'description', label: 'คำอธิบาย', unit: null, kind: 'text' },
  { key: 'ingredient', label: 'ส่วนผสม', unit: null, kind: 'text' },
  { key: 'kcal', label: 'พลังงาน', unit: 'แคล', kind: 'num' },
  { key: 'protein', label: 'โปรตีน', unit: 'กรัม', kind: 'num' },
  { key: 'carb', label: 'คาร์บ', unit: 'กรัม', kind: 'num' },
  { key: 'fat', label: 'ไขมัน', unit: 'กรัม', kind: 'num' },
  { key: 'sugar', label: 'น้ำตาล', unit: 'กรัม', kind: 'num' },
  { key: 'weight', label: 'น้ำหนัก', unit: 'กรัม', kind: 'num' },
]
export const FIELD_KEYS: DishField[] = DEFAULT_FIELDS.map((f) => f.key)
export const NUM_KEYS = new Set<DishField>(['kcal', 'protein', 'carb', 'fat', 'sugar', 'weight'])
export const HIDE_DISH = '_hide'

export const EMPTY_VALUES: DishValues = {
  description: null, ingredient: null, kcal: null, protein: null, carb: null, fat: null, sugar: null, weight: null,
}

/** 33 → "33" · 3.456 → "3.5" · 1200 → "1,200" */
export function numText(n: number): string {
  const r = Math.round(n * 10) / 10
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(r)
}

/** A typed number ("3,5" / " 12 ") → 3.5 / 12, or null when it is not a number ≥ 0. */
export function parseNum(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 ? v : null
  const s = v.trim().replace(/,/g, '.')
  if (!s || !/^\d+(\.\d+)?$/.test(s)) return null
  return Number(s)
}

type DishOverride = Record<string, OverrideValue | boolean>

/** What the AI gets for a dish: POS values with the owner's corrections; hidden fields (or the whole dish) = null. */
export function effectiveValues(values: DishValues, ov: DishOverride | null | undefined): DishValues {
  const out: DishValues = { ...EMPTY_VALUES }
  if (ov?.[HIDE_DISH] === true) return out
  for (const k of FIELD_KEYS) {
    let v: OverrideValue | boolean | undefined = values[k]
    if (ov && k in ov) v = ov[k]
    if (v === null || v === undefined || typeof v === 'boolean') continue
    if (NUM_KEYS.has(k)) (out as unknown as Record<string, number | null>)[k] = parseNum(v)
    else {
      const s = String(v).trim()
      ;(out as unknown as Record<string, string | null>)[k] = s || null
    }
  }
  return out
}

export type FieldState = 'pos' | 'fixed' | 'hidden' | 'empty'

/** pos = POS value used as is · fixed = corrected · hidden = the AI does not get it · empty = nothing in POS */
export function fieldState(k: DishField, pos: DishValues, ov: DishOverride | null | undefined): FieldState {
  if (ov?.[HIDE_DISH] === true) return 'hidden'
  if (ov && k in ov) return ov[k] === null ? 'hidden' : 'fixed'
  const v = pos[k]
  return v === null || v === '' ? 'empty' : 'pos'
}

/** A round dish's overrides as one map entry (null when nothing is changed). */
export function dishOverride(d: Pick<RoundDish, 'overrides' | 'hidden'>): DishOverride | null {
  const out: DishOverride = { ...d.overrides }
  if (d.hidden) out[HIDE_DISH] = true
  return Object.keys(out).length ? out : null
}

export function roundOverrideMap(dishes: Pick<RoundDish, 'id' | 'overrides' | 'hidden'>[]): OverrideMap {
  const map: OverrideMap = {}
  for (const d of dishes) {
    const o = dishOverride(d)
    if (o) map[d.id] = o
  }
  return map
}

/** Dishes with any correction / hidden field. */
export function overrideCount(map: OverrideMap): number {
  return Object.values(map).filter((o) => Object.keys(o).length > 0).length
}

/** A dish shown in review (built from POS + the draft's overrides). */
export function toRoundDish(d: LiveDish, ov: DishOverride | null | undefined): RoundDish {
  const overrides: RoundDish['overrides'] = {}
  for (const k of FIELD_KEYS) if (ov && k in ov) overrides[k] = ov[k] as OverrideValue
  return { ...d, overrides, hidden: ov?.[HIDE_DISH] === true, effective: effectiveValues(d.values, ov) }
}

export interface NutriPill {
  key: DishField
  /** the words before the number (โปรตีน) — or the whole label when there is no number */
  label: string | null
  num: string | null
  unit: string | null
  state: FieldState
  /** the POS number when corrected (struck through) */
  old: string | null
  /** full reading for screen readers / tooltips: "โปรตีน 33 กรัม" */
  title: string
}

/** Nutrition in one line: "180 แคล · โปรตีน 33 ก. · คาร์บ 4 ก. · ไขมัน 3 ก. · น้ำตาล 1 ก. · 150 ก." */
export function nutritionPills(pos: DishValues, ov: DishOverride | null | undefined): NutriPill[] {
  const eff = effectiveValues(pos, ov)
  const spec: [DishField, string, boolean, string][] = [
    ['kcal', 'แคล', true, 'แคล'],
    ['protein', 'โปรตีน', false, 'ก.'],
    ['carb', 'คาร์บ', false, 'ก.'],
    ['fat', 'ไขมัน', false, 'ก.'],
    ['sugar', 'น้ำตาล', false, 'ก.'],
    ['weight', 'น้ำหนัก', true, 'ก.'],
  ]
  return spec.map(([key, word, numFirst, unit]) => {
    const state = fieldState(key, pos, ov)
    const v = eff[key] as number | null
    const p = pos[key] as number | null
    const fullUnit = key === 'kcal' ? 'แคล' : 'กรัม'
    if (state === 'hidden' || state === 'empty' || v === null) {
      return { key, label: word, num: null, unit: null, state: state === 'pos' ? 'empty' : state, old: null,
        title: `${word} ${state === 'hidden' ? 'ซ่อนจาก AI' : 'ไม่มีข้อมูล'}` }
    }
    return {
      key,
      label: numFirst ? null : word,
      num: numText(v),
      unit,
      state,
      old: state === 'fixed' && p !== null ? numText(p) : null,
      title: `${key === 'weight' ? 'น้ำหนัก' : key === 'kcal' ? 'พลังงาน' : word} ${numText(v)} ${fullUnit}`,
    }
  })
}

/** A long description cut on a character count (never inside a Thai vowel/tone mark) + whether it was cut. */
export function clipText(text: string, max: number): { text: string; cut: boolean } {
  if (text.length <= max) return { text, cut: false }
  let end = max
  const space = text.lastIndexOf(' ', max)
  if (space > max - 24) end = space
  // Thai combining marks (above/below vowels, tone marks) must stay with their consonant
  while (end < text.length && /[ัิ-ฺ็-๎]/.test(text[end]!)) end++
  return { text: `${text.slice(0, end).trimEnd()}…`, cut: true }
}

// ------------------------------------------------------------------ notes on the status card

export interface MenuNote {
  level: 'warn' | 'crit' | 'calm'
  text: string
}

/** Expiring / expired / upcoming line on the status card. */
export function statusNote(st: Pick<MenuRoundState, 'status' | 'days_left' | 'current'>): MenuNote | null {
  const c = st.current
  if (!c) return null
  if (st.status === 'expired') {
    return { level: 'crit', text: `หมดอายุแล้วเมื่อ ${thaiDate(c.valid_to, false)} · ตอนนี้ AI ไม่บอกรายละเอียดเมนูจนกว่าจะอนุมัติรอบใหม่` }
  }
  if (st.status === 'expiring') {
    const d = st.days_left ?? 0
    const when = d <= 0 ? 'หมดอายุวันนี้' : `จะหมดอายุในอีก ${d} วัน`
    return { level: 'warn', text: `เมนูรอบนี้${when} (${thaiDate(c.valid_to, false)}) · เตรียมตรวจและอนุมัติรอบใหม่` }
  }
  if (st.status === 'upcoming') {
    return { level: 'calm', text: `รอบนี้เริ่มใช้ ${thaiDate(c.valid_from, false)} · ก่อนหน้านั้น AI ยังไม่บอกรายละเอียดเมนู` }
  }
  return null
}

export interface DriftView {
  headline: string | null
  lines: { label: string; text: string }[]
  /** POS values edited since approval (secondary, muted) */
  changed: string | null
}

const names = (xs: { name: string }[], max = 6): string =>
  xs.length > max ? `${xs.slice(0, max).map((x) => x.name).join(' · ')} และอีก ${xs.length - max} เมนู` : xs.map((x) => x.name).join(' · ')

/** "เมนูใน POS เปลี่ยนจากรอบที่อนุมัติ 3 รายการ · ตรวจแล้วอนุมัติใหม่" + "เพิ่มใน POS: …" / "เลิกขายแล้ว: …" / "เปลี่ยนชื่อ: A → B". */
export function driftView(d: MenuDrift | null): DriftView | null {
  if (!d || (d.count <= 0 && !d.changed.length)) return null
  const lines: DriftView['lines'] = []
  if (d.added.length) lines.push({ label: 'เพิ่มใน POS', text: names(d.added) })
  if (d.removed.length) lines.push({ label: 'เลิกขายแล้ว', text: names(d.removed) })
  if (d.renamed.length) lines.push({ label: 'เปลี่ยนชื่อ', text: d.renamed.map((r) => `${r.from} → ${r.to}`).join(' · ') })
  const changed = d.changed.length
    ? `ข้อมูลใน POS แก้ไข ${d.changed.length} เมนู: ${d.changed
        .slice(0, 4)
        .map((c) => `${c.name} (${c.fields.join(', ')})`)
        .join(' · ')}${d.changed.length > 4 ? ` และอีก ${d.changed.length - 4} เมนู` : ''}`
    : null
  return {
    headline: d.count > 0 ? `เมนูใน POS เปลี่ยนจากรอบที่อนุมัติ ${d.count} รายการ · ตรวจแล้วอนุมัติใหม่` : null,
    lines,
    changed,
  }
}

/** The owner's rail dot / banner: only the owner, only when the server has something to say. */
export function alertDot(alert: MenuAlert | null, role: string | null | undefined): 'none' | 'warn' | 'crit' {
  if (role !== 'owner' || !alert) return 'none'
  return alert.level
}

// ------------------------------------------------------------------ proposals

export interface PendingDish {
  id: string
  name: string
  parts: string[]
}

function fieldMeta(fields: MenuField[], k: string): MenuField | undefined {
  return fields.find((f) => f.key === k) ?? DEFAULT_FIELDS.find((f) => f.key === k)
}

function describe(k: string, v: OverrideValue | boolean | undefined, fields: MenuField[]): string {
  const f = fieldMeta(fields, k)
  const label = f?.label ?? k
  if (v === undefined) return `${label} กลับไปใช้ค่าจาก POS`
  if (v === null) return `ซ่อน${label}`
  if (f?.kind === 'num') {
    const n = parseNum(v as string | number)
    return `${label} ${n === null ? String(v) : numText(n)}${f.unit ? ` ${f.unit}` : ''}`
  }
  const s = String(v)
  return `${label}: ${s.length > 60 ? `${s.slice(0, 60)}…` : s}`
}

/** What a map changes compared with another, per dish ("พลังงาน 200 แคล" / "ซ่อนน้ำตาล" / "ซ่อนทั้งเมนู"). */
export function mapChanges(base: OverrideMap, next: OverrideMap, dishes: { id: string; name: string }[], fields: MenuField[]): PendingDish[] {
  const out: PendingDish[] = []
  const ids = [...new Set([...Object.keys(base), ...Object.keys(next)])]
  for (const id of ids) {
    const a = base[id] ?? {}
    const b = next[id] ?? {}
    const parts: string[] = []
    if ((a[HIDE_DISH] === true) !== (b[HIDE_DISH] === true)) parts.push(b[HIDE_DISH] === true ? 'ซ่อนทั้งเมนู' : 'เลิกซ่อนทั้งเมนู')
    for (const k of FIELD_KEYS) {
      const inA = k in a
      const inB = k in b
      if (!inA && !inB) continue
      if (inA && inB && a[k] === b[k]) continue
      parts.push(describe(k, inB ? b[k] : undefined, fields))
    }
    if (parts.length) out.push({ id, name: dishes.find((d) => d.id === id)?.name ?? `เมนู ${id}`, parts })
  }
  return out
}

function sameNotes(a: MenuNotes, b: MenuNotes): boolean {
  return a.text === b.text && a.new_ids.join() === b.new_ids.join() && a.recommended_ids.join() === b.recommended_ids.join()
}

/** A head's waiting edit of the approved round, as lines for the yellow box. */
export function pendingView(round: MenuRound, fields: MenuField[]): { dishes: PendingDish[]; other: string[] } | null {
  const p = round.pending
  if (!p) return null
  const dishes = p.overrides ? mapChanges(roundOverrideMap(round.dishes), p.overrides, round.dishes, fields) : []
  const other: string[] = []
  if ((p.valid_from && p.valid_from !== round.valid_from) || (p.valid_to && p.valid_to !== round.valid_to)) {
    other.push(`ช่วงเวลา ${periodText(p.valid_from ?? round.valid_from, p.valid_to ?? round.valid_to)}`)
  }
  if (p.poster_media_id !== undefined && p.poster_media_id !== round.poster_media_id) {
    other.push(p.poster_media_id ? 'เปลี่ยนโปสเตอร์เมนู' : 'เอาโปสเตอร์เมนูออก')
  }
  if (p.notes && !sameNotes(p.notes, round.notes)) other.push('แก้เมนูใหม่ เมนูแนะนำ หรือหมายเหตุถึง AI')
  return { dishes, other }
}

/** Dishes changed by the owner/head in a round (for "แก้ 2 เมนู"). */
export function editedCount(round: Pick<MenuRound, 'dishes'>): number {
  return round.dishes.filter((d) => d.hidden || Object.keys(d.overrides).length > 0).length
}

// ------------------------------------------------------------------ review

/** Carry the current round's corrections / notes / poster into a new review, for dishes still on sale. */
export function carryOver(current: MenuRound | null, live: LiveDish[]): { overrides: OverrideMap; notes: MenuNotes; poster_media_id: string | null } {
  const ids = new Set(live.map((d) => d.id))
  const empty = { overrides: {}, notes: { new_ids: [], recommended_ids: [], text: '' }, poster_media_id: null }
  if (!current) return empty
  const overrides: OverrideMap = {}
  for (const [id, o] of Object.entries(roundOverrideMap(current.dishes))) if (ids.has(id)) overrides[id] = { ...o }
  return {
    overrides,
    notes: {
      new_ids: current.notes.new_ids.filter((id) => ids.has(id)),
      recommended_ids: current.notes.recommended_ids.filter((id) => ids.has(id)),
      text: current.notes.text,
    },
    poster_media_id: current.poster_media_id,
  }
}

/** Period problems for the review footer (null = fine). */
export function periodProblem(from: string, to: string, maxDays: number): string | null {
  if (!isYmd(from) || !isYmd(to)) return 'กรุณาเลือกวันเริ่มและวันสิ้นสุด'
  if (to < from) return 'วันสิ้นสุดต้องไม่ก่อนวันเริ่ม'
  if (diffDays(from, to) > maxDays) return `ใช้ได้ไม่เกิน ${maxDays} วันต่อรอบ`
  return null
}

export const ROUND_STATUS_LABEL: Record<string, string> = {
  approved: 'ใช้อยู่',
  pending_approval: 'รอเจ้าของอนุมัติ',
  superseded: 'มีรอบใหม่แทน',
  retired: 'ปิดใช้',
  rejected: 'ไม่อนุมัติ',
}

/** "ผู้จัดการ พลอย" (only heads propose; a name that is already the role label stays as it is). */
export function proposerLabel(name: string | null | undefined): string {
  const n = (name ?? '').trim()
  if (!n || n === 'ผู้จัดการ') return 'ผู้จัดการ'
  return n.startsWith('ผู้จัดการ') ? n : `ผู้จัดการ ${n}`
}

/** Days left for the status line: "เหลือ 61 วัน" · "วันสุดท้าย" */
export function daysLeftText(days: number | null): string | null {
  if (days === null) return null
  return days <= 0 ? 'วันนี้เป็นวันสุดท้าย' : `เหลือ ${days} วัน`
}

/** formatAgo → a phrase after "เสนอ": "24 นาที" → " 24 นาทีที่แล้ว" · "เมื่อวาน" → "เมื่อวาน" · "12 ส.ค." → "เมื่อ 12 ส.ค." */
export function agoText(ago: string): string {
  if (!ago) return ''
  if (/^\d+ (นาที|ชม\.|วัน)$/.test(ago)) return ` ${ago}ที่แล้ว`
  if (ago.startsWith('เมื่อ')) return ago
  return `เมื่อ ${ago}`
}
